import {
    EmploymentStatus,
    type MembershipIncome,
} from '../../income/types/Income';
import type { User } from '../types/Membership';
import {
    HasKids,
    type MembershipProfileAnswerWithQuestion,
    type MembershipProfileKidAnswer,
    type MembershipProfileMoneyGoalAnswer,
    type MembershipProfileRetirementAnswer,
} from '../types/MembershipProfileAnswer';

export interface ProfileCompleteness {
    isPersonalInfoComplete: boolean;
    isFinancialGoalsComplete: boolean;
    isIncomeComplete: boolean;
    isProfileComplete: boolean;
    isKidsDataComplete: boolean;
    isTaxDataComplete: boolean;
    isInsuranceDataComplete: boolean;
    isEmploymentStatusComplete: boolean;
}

export const isMemberPersonalInfoDataComplete = (member: User): boolean => {
    const { dateOfBirth, userAddress, insuranceCoverage } = member;
    const { line1, city, postalCode } = userAddress ?? {};

    return (
        [dateOfBirth, line1, city, postalCode].every(Boolean) &&
        insuranceCoverage?.[0] != null
    );
};

export const isKidsDataComplete = (
    kidAnswer?: MembershipProfileAnswerWithQuestion<MembershipProfileKidAnswer>,
): boolean => {
    if (kidAnswer == null) return false;

    const { answer, additionalInformation } = kidAnswer.answer;

    if (answer === HasKids.YES) {
        // Verify at least one birth year exists
        return additionalInformation?.[0]?.birthYear != null;
    }

    return true;
};

export const isInsuranceDataComplete = (
    primaryUser: User,
    partnerUser: User | null,
): boolean => {
    return [primaryUser, partnerUser]
        .filter((user) => user != null)
        .every((user) => user.insuranceCoverage.length > 0);
};

export const isEmploymentStatusDataComplete = (
    primaryUser: User,
    partnerUser: User | null,
): boolean => {
    return [primaryUser, partnerUser]
        .filter((user) => user != null)
        .every((user) => user?.employmentStatus != null);
};

export const isFinancialGoalsDataComplete = (
    moneyGoalsAnswer?: MembershipProfileAnswerWithQuestion<
        MembershipProfileMoneyGoalAnswer[]
    >,
): boolean =>
    // Verify at least one money goal exists
    moneyGoalsAnswer?.answer[0]?.answer != null;

export const isRetirementDataComplete = (
    retirementAnswer?: MembershipProfileAnswerWithQuestion<MembershipProfileRetirementAnswer>,
): boolean => retirementAnswer?.answer?.answer != null;

export const isTaxDataComplete = (
    primaryUser: User,
    partnerUser: User | null,
): boolean =>
    primaryUser.taxFiling != null &&
    (partnerUser != null ? partnerUser.taxFiling != null : true);

const shouldEmploymentStatusHaveIncome = (
    employmentStatus: EmploymentStatus,
): boolean =>
    employmentStatus === EmploymentStatus.FULL_TIME ||
    employmentStatus === EmploymentStatus.PART_TIME ||
    employmentStatus === EmploymentStatus.FREELANCER ||
    employmentStatus === EmploymentStatus.BUSINESS_OWNER;

export const isIncomeDataComplete = (
    primaryUser: User,
    partnerUser: User | null,
    membershipIncomes: MembershipIncome[],
): boolean => {
    if (
        primaryUser.employmentStatus == null ||
        (partnerUser != null && partnerUser.employmentStatus == null)
    ) {
        return false;
    }

    const primaryMemberIncomeShouldExist = shouldEmploymentStatusHaveIncome(
        primaryUser.employmentStatus,
    );
    const partnerMemberIncomeShouldExist =
        partnerUser?.employmentStatus != null
            ? shouldEmploymentStatusHaveIncome(partnerUser?.employmentStatus)
            : false;

    if (primaryMemberIncomeShouldExist) {
        // Verify primary member income exists
        const primaryIncome = membershipIncomes.find(
            (income) => income.belongsToUserId === primaryUser.id,
        );
        if (primaryIncome == null) {
            return false;
        }
    }

    if (partnerMemberIncomeShouldExist) {
        // Verify partner member income exists
        const partnerIncome = membershipIncomes.find(
            (income) => income.belongsToUserId === partnerUser?.id,
        );
        if (partnerIncome == null) {
            return false;
        }
    }

    return true;
};

export const isMemberProfileDataComplete = (
    primaryUser: User,
    partnerUser: User | null,
    membershipIncomes: MembershipIncome[],
    kidsAnswer?: MembershipProfileAnswerWithQuestion<MembershipProfileKidAnswer>,
    moneyGoalsAnswer?: MembershipProfileAnswerWithQuestion<
        MembershipProfileMoneyGoalAnswer[]
    >,
    retirementAnswer?: MembershipProfileAnswerWithQuestion<MembershipProfileRetirementAnswer>,
): ProfileCompleteness => {
    const isPrimaryPersonalInfoDataComplete =
        isMemberPersonalInfoDataComplete(primaryUser);
    const isPartnerPersonalInfoDataComplete =
        partnerUser != null
            ? isMemberPersonalInfoDataComplete(primaryUser)
            : true;

    const isPersonalInfoComplete =
        isPrimaryPersonalInfoDataComplete &&
        isPartnerPersonalInfoDataComplete &&
        isKidsDataComplete(kidsAnswer);
    const isFinancialGoalsComplete =
        isFinancialGoalsDataComplete(moneyGoalsAnswer) &&
        isRetirementDataComplete(retirementAnswer);
    const isIncomeComplete = isIncomeDataComplete(
        primaryUser,
        partnerUser,
        membershipIncomes,
    );

    const isProfileComplete =
        isPersonalInfoComplete && isFinancialGoalsComplete && isIncomeComplete;

    return {
        isPersonalInfoComplete,
        isFinancialGoalsComplete,
        isIncomeComplete,
        isProfileComplete,
        isKidsDataComplete: isKidsDataComplete(kidsAnswer),
        isTaxDataComplete: isTaxDataComplete(primaryUser, partnerUser),
        isInsuranceDataComplete: isInsuranceDataComplete(
            primaryUser,
            partnerUser,
        ),
        isEmploymentStatusComplete: isEmploymentStatusDataComplete(
            primaryUser,
            partnerUser,
        ),
    };
};
